import React, { useState, useEffect, useRef, useCallback } from 'react';
import { postLog } from '../utils/authApi';

const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

interface DeskToken {
  id: number;
  token_number: number;
  token_code: string;
  customer_name: string;
  customer_phone?: string;
  service?: string;
  notes?: string;
  appointment_time?: string;
  is_walk_in: boolean;
  status: 'waiting' | 'called' | 'hold' | 'urgent' | 'done' | 'skipped' | 'cancelled';
  called_at?: string;
  done_at?: string;
  position_in_queue?: number;
  estimated_wait_mins?: number;
}

interface DeskState {
  counter: { code: string; name: string; description: string; current_token: number; outlet_id?: number | null };
  org_id: string;
  queue_name: string;
  customer_label: string;
  counter_label: string;
  is_open: boolean;
  avg_wait_mins: number;
  waiting_count: number;
  current_token: number;
  called_token: DeskToken | null;
  tokens_today: DeskToken[];
  tts_lang_1?: string;
  tts_lang_2?: string;
  tts_lang_3?: string;
  tts_gender?: 'female' | 'male';
}

// ── Alarm sound ───────────────────────────────────────────────────────────────

function playAlarm() {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const times = [0, 0.3, 0.6];
    times.forEach(t => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.frequency.value = 880;
      o.type = 'sine';
      g.gain.setValueAtTime(0.6, ctx.currentTime + t);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + 0.25);
      o.start(ctx.currentTime + t);
      o.stop(ctx.currentTime + t + 0.25);
    });
  } catch {}
}

// ── Speak token number ────────────────────────────────────────────────────────

const DESK_ANNOUNCEMENTS: Record<string, (d: string, c: string) => string> = {
  en: (d, c) => `Token number ${d}. Please proceed to ${c}.`,
  hi: (d, c) => `टोकन नंबर ${d}. कृपया ${c} पर जाएं।`,
  ar: (d, c) => `رقم التوكن ${d.replace(/[0-9]/g, n => '٠١٢٣٤٥٦٧٨٩'[+n])}. يرجى التوجه إلى ${c}.`,
  ta: (d, c) => `டோக்கன் எண் ${d}. தயவுசெய்து ${c} க்கு செல்லவும்.`,
  te: (d, c) => `టోకెన్ నంబర్ ${d}. దయచేసి ${c} కి వెళ్ళండి.`,
  kn: (d, c) => `ಟೋಕನ್ ನಂಬರ್ ${d}. ದಯವಿಟ್ಟು ${c} ಗೆ ಹೋಗಿ.`,
  ml: (d, c) => `ടോക്കൺ നമ്പർ ${d}. ദയവായി ${c} ലേക്ക് പോകൂ.`,
  mr: (d, c) => `टोकन क्रमांक ${d}. कृपया ${c} कडे जा.`,
  gu: (d, c) => `ટોકન નંબર ${d}. કૃपा करके ${c} पर जाएं।`,
  pa: (d, c) => `ਟੋਕਨ ਨੰਬਰ ${d}. ਕਿਰਪਾ ਕਰਕੇ ${c} ਤੇ ਜਾਓ।`,
  bn: (d, c) => `টোকেন নম্বর ${d}. অনুগ্রহ করে ${c} এ যান।`,
  ur: (d, c) => `ٹوکن نمبر ${d}. براہ کرم ${c} پر جائیں۔`,
  fr: (d, c) => `Numéro de ticket ${d}. Veuillez vous rendre au ${c}.`,
  es: (d, c) => `Número de turno ${d}. Por favor diríjase a ${c}.`,
  de: (d, c) => `Tokennummer ${d}. Bitte gehen Sie zu ${c}.`,
  'zh-CN': (d, c) => `号码 ${d}。请前往 ${c}。`,
  ja: (d, c) => `番号 ${d}。${c} へお越しください。`,
  ko: (d, c) => `번호 ${d}. ${c} 로 오십시오.`,
  ru: (d, c) => `Номер талона ${d}. Пожалуйста пройдите к ${c}.`,
  tr: (d, c) => `Token numarası ${d}. Lütfen ${c} e gidin.`,
  vi: (d, c) => `Số thứ tự ${d}. Vui lòng đến ${c}.`,
  th: (d, c) => `หมายเลข ${d}. กรุณาไปที่ ${c}.`,
  id: (d, c) => `Nomor token ${d}. Silakan menuju ke ${c}.`,
  ms: (d, c) => `Nombor token ${d}. Sila pergi ke ${c}.`,
  it: (d, c) => `Numero token ${d}. Si prega di recarsi a ${c}.`,
  nl: (d, c) => `Tokennummer ${d}. Gaat u alstublieft naar ${c}.`,
  pl: (d, c) => `Numer tokenu ${d}. Proszę udać się do ${c}.`,
  uk: (d, c) => `Номер талона ${d}. Будь ласка пройдіть до ${c}.`,
  ro: (d, c) => `Numărul tokenului ${d}. Vă rugăm să mergeți la ${c}.`,
  el: (d, c) => `Αριθμός token ${d}. Παρακαλώ προχωρήστε στο ${c}.`,
  cs: (d, c) => `Číslo tokenu ${d}. Prosím jděte k ${c}.`,
  hu: (d, c) => `Token szám ${d}. Kérjük menjen a ${c}.`,
  fi: (d, c) => `Token numero ${d}. Ole hyvä ja mene ${c}.`,
  sv: (d, c) => `Tokennummer ${d}. Vänligen gå till ${c}.`,
  no: (d, c) => `Tokennummer ${d}. Vennligst gå til ${c}.`,
  da: (d, c) => `Tokennummer ${d}. Gå venligst til ${c}.`,
  he: (d, c) => `מספר תור ${d}. אנא פנה אל ${c}.`,
  fa: (d, c) => `شماره نوبت ${d}. لطفاً به ${c} بروید.`,
  si: (d, c) => `ටෝකන් අංකය ${d}. කරුණාකර ${c} වෙත යන්න.`,
  ne: (d, c) => `टोकन नम्बर ${d}. कृपया ${c} मा जानुहोस्।`,
  sw: (d, c) => `Nambari ya token ${d}. Tafadhali nenda kwa ${c}.`,
// European extras
bg: (d, c) => `Номер на жетона ${d}. Моля отидете до ${c}.`,
hr: (d, c) => `Broj tokena ${d}. Molimo idite do ${c}.`,
sk: (d, c) => `Číslo tokenu ${d}. Prosím choďte k ${c}.`,
sl: (d, c) => `Številka žetona ${d}. Prosimo pojdite k ${c}.`,
sr: (d, c) => `Broj tokena ${d}. Molimo idite do ${c}.`,
lt: (d, c) => `Žetono numeris ${d}. Prašome eiti prie ${c}.`,
lv: (d, c) => `Žetona numurs ${d}. Lūdzu dodieties uz ${c}.`,
et: (d, c) => `Žetoni number ${d}. Palun minge ${c} juurde.`,
is: (d, c) => `Númer tákns ${d}. Vinsamlegast farðu til ${c}.`,
mk: (d, c) => `Број на токен ${d}. Ве молиме одете кај ${c}.`,
sq: (d, c) => `Numri i tokenit ${d}. Ju lutemi shkoni tek ${c}.`,
bs: (d, c) => `Broj tokena ${d}. Molimo idite do ${c}.`,
ca: (d, c) => `Número de torn ${d}. Si us plau aneu a ${c}.`,
gl: (d, c) => `Número de quenda ${d}. Por favor vai a ${c}.`,
cy: (d, c) => `Rhif tocyn ${d}. Ewch i ${c} os gwelwch yn dda.`,
ga: (d, c) => `Uimhir comhartha ${d}. Téigh go ${c} le do thoil.`,
mt: (d, c) => `Numru tal-token ${d}. Jekk jogħġbok mur ${c}.`,
pt: (d, c) => `Número do token ${d}. Por favor dirija-se a ${c}.`,
af: (d, c) => `Token nommer ${d}. Gaan asseblief na ${c}.`,
ps: (d, c) => `د ټوکن شمیره ${d}. مهرباني وکړئ ${c} ته لاړ شئ.`,
// Asian extras
mn: (d, c) => `Токений дугаар ${d}. ${c} рүү явна уу.`,
jv: (d, c) => `Nomer token ${d}. Mangga menyang ${c}.`,
su: (d, c) => `Nomer token ${d}. Punten angkat ka ${c}.`,
fil: (d, c) => `Token numero ${d}. Mangyaring pumunta sa ${c}.`,
ka: (d, c) => `ტოკენის ნომერი ${d}. გთხოვთ გახვიდეთ ${c}-ში.`,
kk: (d, c) => `Токен нөмірі ${d}. ${c} жаққа өтіңіз.`,
az: (d, c) => `Token nömrəsi ${d}. Zəhmət olmasa ${c} a keçin.`,
uz: (d, c) => `Token raqami ${d}. Iltimos ${c} ga o'ting.`,
};

// ── Browser Web Speech Fallback (Male & Female) ──
function speakWithBrowser(text: string, lang = 'en', gender: 'female' | 'male' = 'female'): Promise<void> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      resolve();
      return;
    }
    try {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = lang;
      utterance.rate = 0.9;

      const voices = window.speechSynthesis.getVoices();
      const matching = voices.filter(v => v.lang.toLowerCase().startsWith(lang.split('-')[0].toLowerCase()));

      const maleKeywords = ['david', 'mark', 'george', 'ravi', 'guy', 'alex', 'fred', 'male', 'madhur'];
      const femaleKeywords = ['zira', 'samantha', 'victoria', 'heera', 'aria', 'female', 'swara', 'pallavi', 'sapna'];
      const targetKeywords = gender === 'male' ? maleKeywords : femaleKeywords;

      const picked = matching.find(v => targetKeywords.some(k => v.name.toLowerCase().includes(k))) || matching[0] || voices[0];
      if (picked) utterance.voice = picked;

      let done = false;
      utterance.onend = () => { if (!done) { done = true; resolve(); } };
      utterance.onerror = () => { if (!done) { done = true; resolve(); } };
      setTimeout(() => { if (!done) { done = true; resolve(); } }, 6000);

      window.speechSynthesis.speak(utterance);
    } catch {
      resolve();
    }
  });
}

async function speakToken(tokenNum: number, counterName: string, deskState?: DeskState | null) {
  const digits = String(tokenNum).padStart(3, '0').split('').join(' ');
  // Prefer the actual configured languages from the outlet's settings
  // (fetched with the desk state) — localStorage is never populated on
  // this page, so falling back to it silently drops all but English.
  const langs = deskState
    ? [deskState.tts_lang_1, deskState.tts_lang_2, deskState.tts_lang_3].filter(Boolean) as string[]
    : [];
  if (langs.length === 0) langs.push('en');

  const playNext = async (index: number): Promise<void> => {
    if (index >= langs.length) return;
    const lang = langs[index];
    const fn = DESK_ANNOUNCEMENTS[lang] || DESK_ANNOUNCEMENTS['en'];
    const text = fn(digits, counterName);
    const gender = deskState?.tts_gender || 'female';
    const fallback = () => {
      speakWithBrowser(text, lang, gender).finally(() => playNext(index + 1));
    };

    try {
      const response = await fetch(`${BASE}/queue/tts?text=${encodeURIComponent(text)}&lang=${lang}&gender=${gender}`);
      if (!response.ok) throw new Error('Server TTS failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.volume = 1;
      audio.onended = () => { URL.revokeObjectURL(url); playNext(index + 1); };
      audio.onerror = () => {
        URL.revokeObjectURL(url);
        fallback();
      };
      await audio.play().catch(() => {
        URL.revokeObjectURL(url);
        fallback();
      });
    } catch {
      fallback();
    }
  };

  playNext(0);
}

export default function QueueDeskPage() {
  const params = new URLSearchParams(window.location.search);
  const counterCode = params.get('counter') || '';
  const [darkMode, setDarkMode] = useState(false);
  const [desk, setDesk] = useState<DeskState | null>(null);
  const [error, setError] = useState('');
  const [calling, setCalling] = useState(false);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [tab, setTab] = useState<'active' | 'all'>('active');
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [lastCalledToken, setLastCalledToken] = useState<number | null>(null);
const [customAnnounceText, setCustomAnnounceText] = useState('');
const [customAnnounceLang, setCustomAnnounceLang] = useState('en');
const [customAnnouncing, setCustomAnnouncing] = useState(false);
const [customAnnounceGender, setCustomAnnounceGender] = useState<'female' | 'male'>('female');
const [savedAnnouncements, setSavedAnnouncements] = useState<any[]>(
  JSON.parse(localStorage.getItem('saved_announcements') || '[]')
);
const [showAnnouncePanel, setShowAnnouncePanel] = useState(false);
  const intervalRef = useRef<any>(null);
  const bg = darkMode ? '#0f172a' : '#f8fafc';
const cardBg = darkMode ? '#1e293b' : '#ffffff';
const textPrimary = darkMode ? '#f1f5f9' : '#0f172a';
const textMuted = darkMode ? '#94a3b8' : '#64748b';
const border = darkMode ? '#334155' : '#e2e8f0';
  const fetchDesk = useCallback(async () => {
    if (!counterCode) { setError('No counter code in URL'); return; }
    try {
      const r = await fetch(`${BASE}/queue/desk/${counterCode}`);
      if (!r.ok) { setError(`Counter not found (${r.status})`); return; }
      const data: DeskState = await r.json();
      setDesk(prev => {
        // Play alarm + speak if new token called
        if (prev && data.current_token !== prev.current_token && data.current_token > 0) {
          playAlarm();
          if (voiceEnabled) speakToken(data.current_token, data.counter?.name || 'this counter', data);
          setLastCalledToken(data.current_token);
        }
        return data;
      });
      setError('');
    } catch {
      setError('Cannot reach server');
    }
  }, [counterCode, voiceEnabled]);

  useEffect(() => {
    fetchDesk();
    intervalRef.current = setInterval(fetchDesk, 3000);
    return () => clearInterval(intervalRef.current);
  }, [fetchDesk]);

  const callNext = async () => {
    setCalling(true);
    try {
      const r = await fetch(`${BASE}/queue/desk/${counterCode}/call-next`, { method: 'POST' });
      if (!r.ok) {
        const t = await r.json();
        if (t.detail === 'No waiting tokens') alert('Queue is empty!');
      } else {
        const data = await r.json();
        postLog('DESK_CALL_NEXT', `Call Next at ${desk?.counter.name || counterCode} | ${desk?.queue_name || ''}`);
        await fetchDesk();
        // Use data from response OR fall back to desk state after refresh
        const tokenNum = data?.token_number || data?.current_token || desk?.current_token;
        const customerName = data?.customer_name || '';
        const counterName = desk?.counter.name || counterCode;
        if (tokenNum) {
  playAlarm();
  if (voiceEnabled) speakToken(tokenNum, counterName, desk);
}
      }
    } catch {}
    setCalling(false);
  };

  const doAction = async (tokenId: number, action: string) => {
    setActionLoading(tokenId);
    const token = desk?.tokens_today.find(t => t.id === tokenId);
    await fetch(`${BASE}/queue/desk/${counterCode}/action/${tokenId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action }),
    });
    postLog('DESK_TOKEN_ACTION', `${action.toUpperCase()} — Token #${token ? String(token.token_number).padStart(3,'0') : tokenId} | ${token?.customer_name || ''}${token?.service ? ` | ${token.service}` : ''} | Counter: ${desk?.counter.name || counterCode}`);
    await fetchDesk();
    if (action === 'call' && token) {
      playAlarm();
      if (voiceEnabled) speakToken(token.token_number, desk?.counter.name || counterCode, desk);
    }
    setActionLoading(null);
  };

  if (!counterCode) return (
    <div style={centerStyle}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>❌</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Missing counter code</div>
      <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 8 }}>URL must include <code>?counter=XXXXXX</code></div>
    </div>
  );

  if (error) return (
    <div style={centerStyle}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: '#ef4444' }}>{error}</div>
      <button onClick={fetchDesk} style={btnStyle('#0f172a', '#fff', '12px 24px', 14)}>Retry</button>
    </div>
  );

  if (!desk) return (
    <div style={centerStyle}>
      <div style={{ width: 36, height: 36, border: '3px solid #e2e8f0', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginBottom: 16 }} />
      <div style={{ color: '#94a3b8', fontSize: 14 }}>Loading desk...</div>
    </div>
  );

  const tokens = desk.tokens_today ?? [];
  const active = tokens.filter(t => ['waiting', 'urgent', 'hold', 'called'].includes(t.status));
  const waiting = tokens.filter(t => ['waiting', 'urgent'].includes(t.status));
  const displayed = tab === 'active' ? active : tokens;

  return (
    <div style={{
      minHeight: '100vh',
      background: bg,
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      display: 'flex',
      flexDirection: 'column'
    }}>

      {/* ── Top bar ── */}
      <div style={{ background: cardBg, borderBottom: `1px solid ${border}`, padding: '14px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: textMuted, letterSpacing: 2, textTransform: 'uppercase' }}>{desk.queue_name}</div>
            <div style={{ fontSize: 19, fontWeight: 900, color: textPrimary, marginTop: 3, letterSpacing: -0.5 }}>{desk.counter.name}</div>
            <div style={{ fontSize: 10, fontWeight: 600, color: '#6366f1', letterSpacing: 1.5, textTransform: 'uppercase', marginTop: 2 }}>Counter Desk</div>
          </div>
          <span style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 20, flexShrink: 0,
            border: `1.5px solid ${desk.is_open ? '#22c55e' : '#ef4444'}`,
            background: desk.is_open ? (darkMode ? 'rgba(34,197,94,0.12)' : '#f0fdf4') : (darkMode ? 'rgba(239,68,68,0.12)' : '#fef2f2'),
          }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: desk.is_open ? '#22c55e' : '#ef4444' }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: desk.is_open ? '#16a34a' : '#dc2626' }}>
              {desk.is_open ? 'OPEN' : 'CLOSED'}
            </span>
          </span>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          {[
            { label: darkMode ? 'Light' : 'Dark', icon: darkMode ? '☀️' : '🌙', active: false, onClick: () => setDarkMode(d => !d) },
            { label: 'Announce', icon: '📢', active: showAnnouncePanel, onClick: () => setShowAnnouncePanel(p => !p) },
            { label: voiceEnabled ? 'Voice On' : 'Voice Off', icon: voiceEnabled ? '🔊' : '🔇', active: voiceEnabled, onClick: () => setVoiceEnabled(v => !v) },
          ].map(b => (
            <button key={b.label} onClick={b.onClick}
              style={{
                flex: 1, padding: '9px 6px', borderRadius: 10, cursor: 'pointer',
                border: `1.5px solid ${b.active ? '#4f46e5' : border}`,
                background: b.active ? (darkMode ? 'rgba(79,70,229,0.14)' : '#eef2ff') : 'transparent',
                color: b.active ? '#4f46e5' : textMuted,
                fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
              }}>
              <span>{b.icon}</span>{b.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0 }}>

{/* ── Custom Announcement Panel ── */}
{showAnnouncePanel && (
  <div onClick={() => setShowAnnouncePanel(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
  <div onClick={e => e.stopPropagation()} style={{ background: cardBg, borderRadius: 16, border: `1px solid ${border}`, width: '100%', maxWidth: 420, maxHeight: '85vh', overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ fontSize: 15, fontWeight: 800, color: textPrimary }}>📢 Custom Announcement</div>
      <button onClick={() => setShowAnnouncePanel(false)} style={{ width: 28, height: 28, borderRadius: 8, border: 'none', background: darkMode ? '#334155' : '#f1f5f9', color: textMuted, fontSize: 16, cursor: 'pointer', lineHeight: '28px' }}>×</button>
    </div>
    <textarea
      value={customAnnounceText}
      onChange={e => setCustomAnnounceText(e.target.value)}
      placeholder="Type anything to announce..."
      rows={2}
      style={{ width: '100%', borderRadius: 8, border: `1.5px solid ${border}`, padding: '8px 10px', fontSize: 12, background: cardBg, color: textPrimary, resize: 'vertical', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }}
    />

<div style={{ display: 'flex', gap: 4 }}>
  <button
    onClick={() => setCustomAnnounceGender('female')}
    style={{
      flex: 1, padding: '7px', borderRadius: 8, border: '1.5px solid',
      borderColor: customAnnounceGender === 'female' ? '#ec4899' : border,
      background: customAnnounceGender === 'female' ? '#fdf2f8' : 'transparent',
      color: customAnnounceGender === 'female' ? '#ec4899' : textMuted,
      fontWeight: 700, fontSize: 11, cursor: 'pointer'
    }}>
    👩 Female
  </button>
  <button
    onClick={() => setCustomAnnounceGender('male')}
    style={{
      flex: 1, padding: '7px', borderRadius: 8, border: '1.5px solid',
      borderColor: customAnnounceGender === 'male' ? '#6366f1' : border,
      background: customAnnounceGender === 'male' ? '#eef2ff' : 'transparent',
      color: customAnnounceGender === 'male' ? '#6366f1' : textMuted,
      fontWeight: 700, fontSize: 11, cursor: 'pointer'
    }}>
    👨 Male
  </button>
</div>

    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      <select value={customAnnounceLang} onChange={e => setCustomAnnounceLang(e.target.value)}
        style={{ flex: 1, minWidth: 120, borderRadius: 8, border: `1.5px solid ${border}`, padding: '7px 10px', fontSize: 12, background: cardBg, color: textPrimary, outline: 'none' }}>
        <option value="en">English</option>
        <option value="hi">Hindi</option>
        <option value="ur">Urdu</option>
        <option value="ar">Arabic</option>
        <option value="ta">Tamil</option>
        <option value="te">Telugu</option>
        <option value="kn">Kannada</option>
        <option value="ml">Malayalam</option>
        <option value="mr">Marathi</option>
        <option value="gu">Gujarati</option>
        <option value="bn">Bengali</option>
        <option value="pa">Punjabi</option>
        <option value="fr">French</option>
        <option value="es">Spanish</option>
        <option value="de">German</option>
        <option value="ru">Russian</option>
        <option value="zh-CN">Chinese</option>
        <option value="ja">Japanese</option>
        <option value="ko">Korean</option>
        <option value="tr">Turkish</option>
        <option value="vi">Vietnamese</option>
        <option value="th">Thai</option>
        <option value="id">Indonesian</option>
        <option value="ms">Malay</option>
        <option value="sw">Swahili</option>
        <option value="fil">Filipino</option>
      </select>
      <button
       onClick={async () => {
  if (!customAnnounceText.trim() || customAnnouncing) return;
  setCustomAnnouncing(true);

  // Declare OUTSIDE the try block so catch can access it
  let textToSpeak = customAnnounceText;

  try {
    if (customAnnounceLang !== 'en') {
      try {
        const tr = await fetch(`${BASE}/translate?text=${encodeURIComponent(customAnnounceText)}&tl=${customAnnounceLang}`);
        const td = await tr.json();
        if (td.translated) textToSpeak = td.translated;
      } catch {}
    }
    await fetch(`${BASE}/queue/announce`, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ text: textToSpeak, lang: customAnnounceLang, gender: customAnnounceGender, org_id: desk?.org_id || counterCode, outlet_id: desk?.counter?.outlet_id || null }),
}).catch(() => {});
    const r = await fetch(`${BASE}/queue/tts?text=${encodeURIComponent(textToSpeak)}&lang=${customAnnounceLang}&gender=${customAnnounceGender}`);
            if (!r.ok) throw new Error('Server TTS failed');
            const blob = await r.blob();
            if (blob.size === 0) throw new Error('Empty audio');
            const url = URL.createObjectURL(blob);
            const audio = new Audio(url);
            audio.onended = () => { URL.revokeObjectURL(url); setCustomAnnouncing(false); };
            audio.onerror = async () => {
              URL.revokeObjectURL(url);
              await speakWithBrowser(textToSpeak, customAnnounceLang, customAnnounceGender);
              setCustomAnnouncing(false);
            };
            await audio.play().catch(async () => {
              URL.revokeObjectURL(url);
              await speakWithBrowser(textToSpeak, customAnnounceLang, customAnnounceGender);
              setCustomAnnouncing(false);
            });
          } catch (err) {
            // Server failed -> fallback to browser voice immediately
            await speakWithBrowser(textToSpeak, customAnnounceLang, customAnnounceGender);
            setCustomAnnouncing(false);
          }
        }}
        disabled={customAnnouncing || !customAnnounceText.trim()}
        style={btnStyle(customAnnounceText.trim() ? '#6366f1' : '#334155', customAnnounceText.trim() ? '#fff' : '#64748b', '8px 16px', 12)}>
        {customAnnouncing ? '🔊...' : '🔊 Speak'}
      </button>
      <button
        onClick={() => {
          if (!customAnnounceText.trim()) return;
          const name = prompt('Save as:');
          if (!name?.trim()) return;
          const saved = JSON.parse(localStorage.getItem('saved_announcements') || '[]');
          saved.push({ id: Date.now(), name: name.trim(), text: customAnnounceText, lang: customAnnounceLang, gender: customAnnounceGender });
          localStorage.setItem('saved_announcements', JSON.stringify(saved));
          setSavedAnnouncements([...saved]);
        }}
        disabled={!customAnnounceText.trim()}
        style={btnStyle(customAnnounceText.trim() ? '#15803d' : '#334155', customAnnounceText.trim() ? '#fff' : '#64748b', '8px 16px', 12)}>
        💾 Save
      </button>
    </div>

    {/* Saved list */}
    {savedAnnouncements.length > 0 && (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Saved</div>
        {savedAnnouncements.map((s: any) => (
          <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, background: darkMode ? '#0f172a' : '#f8fafc', border: `1px solid ${border}`, borderRadius: 8, padding: '8px 12px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: textPrimary }}>{s.name}</div>
              <div style={{ fontSize: 10, color: textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.text}</div>
            </div>
            <span style={{ fontSize: 9, background: '#312e81', color: '#a5b4fc', borderRadius: 99, padding: '2px 6px', fontWeight: 700, flexShrink: 0 }}>{s.lang}</span>
            <button
             onClick={async () => {
  setCustomAnnouncing(true);
  try {
    await fetch(`${BASE}/queue/announce`, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ text: s.text, lang: s.lang, gender: s.gender || 'female', org_id: desk?.org_id || counterCode, outlet_id: desk?.counter?.outlet_id || null }),
}).catch(() => {});
    const r = await fetch(`${BASE}/queue/tts?text=${encodeURIComponent(s.text)}&lang=${s.lang}&gender=${s.gender || 'female'}`);
                  if (!r.ok) throw new Error('Server TTS failed');
                  const blob = await r.blob();
                  const url = URL.createObjectURL(blob);
                  const audio = new Audio(url);
                  audio.onended = () => { URL.revokeObjectURL(url); setCustomAnnouncing(false); };
                  audio.onerror = async () => {
                    URL.revokeObjectURL(url);
                    await speakWithBrowser(s.text, s.lang, s.gender || 'female');
                    setCustomAnnouncing(false);
                  };
                  await audio.play().catch(async () => {
                    URL.revokeObjectURL(url);
                    await speakWithBrowser(s.text, s.lang, s.gender || 'female');
                    setCustomAnnouncing(false);
                  });
                } catch {
                  await speakWithBrowser(s.text, s.lang, s.gender || 'female');
                  setCustomAnnouncing(false);
                }
              }}
              style={btnStyle('#6366f1', '#fff', '5px 10px', 11)}>🔊</button>
            <button
              onClick={() => {
                const updated = savedAnnouncements.filter((a: any) => a.id !== s.id);
                localStorage.setItem('saved_announcements', JSON.stringify(updated));
                setSavedAnnouncements(updated);
              }}
              style={btnStyle('#dc2626', '#fff', '5px 10px', 11)}>🗑</button>
          </div>
        ))}
      </div>
    )}
  </div>
  </div>
)}



        {/* ── Current token hero ── */}
        <div style={{ background: darkMode ? 'linear-gradient(135deg, #1e293b, #0f172a)' : '#f8fafc', padding: '32px 24px', textAlign: 'center', borderBottom: `1px solid ${border}` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8 }}>
            NOW SERVING
          </div>
          <div style={{
            fontSize: 96, fontWeight: 900, letterSpacing: 8, lineHeight: 1,
            color: desk.current_token > 0 ? textPrimary : border,
            textShadow: desk.current_token > 0 ? '0 0 40px rgba(99,102,241,0.4)' : 'none',
          }}>
            {desk.current_token > 0 ? String(desk.current_token).padStart(3, '0') : '---'}
          </div>
          {desk.called_token && (
            <div style={{ marginTop: 10, fontSize: 16, fontWeight: 700, color: '#94a3b8' }}>
              {desk.called_token.customer_name}
              {desk.called_token.service && <span style={{ fontSize: 13, color: '#64748b', marginLeft: 10 }}>{desk.called_token.service}</span>}
            </div>
          )}

          {/* Stats row */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 32, marginTop: 20 }}>
            {[
              { label: 'Waiting', value: waiting.length, color: '#6366f1' },
              { label: 'Done', value: tokens.filter(t => t.status === 'done').length, color: '#22c55e' },
              { label: 'Total', value: tokens.length, color: '#94a3b8' },
            ].map(s => (
              <div key={s.label} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 28, fontWeight: 900, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 10, color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Call Next button ── */}
        <div style={{ padding: '16px 24px', background: bg, borderBottom: `1px solid ${border}` }}>
          <button onClick={callNext} disabled={calling || waiting.length === 0}
            style={{
              width: '100%', padding: '18px', borderRadius: 14, border: 'none', cursor: waiting.length > 0 ? 'pointer' : 'not-allowed',
              background: waiting.length > 0
                ? 'linear-gradient(135deg, #6366f1, #4f46e5)'
                : (darkMode ? '#1e293b' : '#f1f5f9'),
              color: waiting.length > 0 ? '#fff' : textMuted,
              fontSize: 18, fontWeight: 900, letterSpacing: 0.5,
              boxShadow: waiting.length > 0 ? '0 4px 24px rgba(79,70,229,0.3)' : 'none',
              transition: 'all 0.2s',
            }}>
            {calling ? '📢 Calling...' : waiting.length > 0 ? `📢 CALL NEXT  ·  ${waiting.length} waiting` : '🎉 Queue Clear'}
          </button>
        </div>

        {/* ── Called token actions ── */}
        {desk.called_token && (
          <div style={{
            padding: '16px 24px',
            background: darkMode ? '#131c2e' : '#eef2ff',
            borderBottom: `1px solid ${darkMode ? '#252f45' : '#c7d2fe'}`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: darkMode ? '#818cf8' : '#4f46e5', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>
                  Currently Serving
                </div>
                <div style={{ fontSize: 15, fontWeight: 800, color: textPrimary }}>
                  {desk.called_token.customer_name}
                  {desk.called_token.customer_phone && (
                    <span style={{ color: textMuted, marginLeft: 10, fontSize: 12, fontWeight: 500 }}>📱 {desk.called_token.customer_phone}</span>
                  )}
                </div>
              </div>
              <button onClick={async () => {
                playAlarm();
                if (voiceEnabled) speakToken(desk.called_token!.token_number, desk.counter.name, desk);
                postLog('DESK_RING_AGAIN', `Ring Again — Token #${String(desk.called_token!.token_number).padStart(3,'0')} | ${desk.called_token!.customer_name} | Counter: ${desk.counter.name}`);
                await fetch(`${BASE}/queue/desk/${counterCode}/ring-again`, { method: 'POST' });
              }} style={btnStyle('#4f46e5', '#fff', '10px 18px', 13)}>📢 Ring Again</button>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={() => doAction(desk.called_token!.id, 'attended')} disabled={actionLoading === desk.called_token.id}
                style={btnStyle('#16a34a', '#fff', '9px 16px', 12)}>✅ Attended</button>
              <button onClick={() => doAction(desk.called_token!.id, 'no_show')} disabled={actionLoading === desk.called_token.id}
                style={btnStyle(darkMode ? '#3a1a1a' : '#fee2e2', darkMode ? '#fca5a5' : '#b91c1c', '9px 16px', 12)}>🚫 No Show</button>
              <button onClick={() => doAction(desk.called_token!.id, 'hold')} disabled={actionLoading === desk.called_token.id}
                style={btnStyle(darkMode ? '#332811' : '#fef3c7', darkMode ? '#fbbf24' : '#92400e', '9px 16px', 12)}>⏸ Hold</button>
              <button onClick={() => doAction(desk.called_token!.id, 'skip')} disabled={actionLoading === desk.called_token.id}
                style={btnStyle('#ffffff', '#334155', '9px 16px', 12)}>⏭ Skip</button>
            </div>

            <div style={{ marginTop: 10, fontSize: 11, color: textMuted }}>
              💡 Physically present → <strong style={{ color: '#16a34a' }}>Attended</strong> &nbsp;·&nbsp;
              Didn't show up → <strong style={{ color: '#dc2626' }}>No Show</strong> &nbsp;·&nbsp;
              Come back later → <strong style={{ color: '#d97706' }}>Hold</strong>
            </div>
          </div>
        )}
        {/* ── Token list ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px', background: bg }}>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 14 }}>
            {[
              { id: 'active', label: `Active (${active.length})` },
              { id: 'all', label: `All (${tokens.length})` },
            ].map(t => (
              <button key={t.id} onClick={() => setTab(t.id as any)}
                style={{ padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, background: tab === t.id ? '#fff' : 'transparent', color: tab === t.id ? '#0f172a' : '#64748b' }}>
                {t.label}
              </button>
            ))}
          </div>

          {displayed.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: '#334155' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🎫</div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>No tokens yet</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {displayed
                .sort((a, b) => {
                  const o: Record<string, number> = { urgent: 0, called: 1, waiting: 2, hold: 3, done: 9, skipped: 9, cancelled: 9, no_show: 9 };
                  return (o[a.status] ?? 5) - (o[b.status] ?? 5) || a.token_number - b.token_number;
                })
                .map(token => {
                  const isActive = ['waiting', 'urgent', 'hold', 'called'].includes(token.status);
                  const isCalled = token.status === 'called';
                  return (
                    <div key={token.id} style={{
                      background: isCalled
                        ? (darkMode ? 'rgba(79,70,229,0.14)' : '#eef2ff')
                        : (darkMode ? '#1e293b' : '#fff'),
                      border: `1px solid ${isCalled ? '#4f46e5' : token.status === 'urgent' ? '#f97316' : border}`,
                      borderRadius: 12, padding: '12px 16px',
                      opacity: ['done', 'skipped', 'cancelled', 'no_show'].includes(token.status) ? 0.5 : 1,
                      display: 'flex', alignItems: 'center', gap: 14,
                    }}>
                      {/* Token number */}
                      <div style={{
                        width: 48, height: 48, borderRadius: 10, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 16, fontWeight: 900, color: '#fff',
                        background: token.status === 'urgent' ? '#ea580c' : isCalled ? '#4f46e5' : token.status === 'hold' ? '#92400e' : '#6366f1',
                      }}>
                        {String(token.token_number).padStart(3, '0')}
                      </div>

                      {/* Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: textPrimary }}>{token.customer_name}</div>
                        <div style={{ fontSize: 11, color: textMuted, marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                          {token.service && <span>🔧 {token.service}</span>}
                          {token.customer_phone && <span>📱 {token.customer_phone}</span>}
                          {token.appointment_time && <span>🕐 {token.appointment_time}</span>}
                          {token.is_walk_in ? <span>🚶 Walk-in</span> : <span>📅 Appt</span>}
                          {token.status === 'waiting' && token.position_in_queue && (
                            <span style={{ color: '#6366f1' }}>#{token.position_in_queue} · ~{token.estimated_wait_mins}m</span>
                          )}
                        </div>
                        {token.notes && <div style={{ fontSize: 11, color: '#475569', marginTop: 3, fontStyle: 'italic' }}>{token.notes}</div>}
                      </div>

                      {/* Status badge */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flexShrink: 0 }}>
                        {isActive && !isCalled && (
                          <button onClick={() => doAction(token.id, 'call')} disabled={actionLoading === token.id}
                            style={btnStyle('#16a34a', '#fff', '5px 10px', 11)}>📢 Call</button>
                        )}
                        {isActive && (
                          <button onClick={() => doAction(token.id, 'done')} disabled={actionLoading === token.id}
                            style={btnStyle('#166534', '#fff', '5px 10px', 11)}>✅ Done</button>
                        )}
                        {!isActive && (
                          <button onClick={() => doAction(token.id, 'restore')} disabled={actionLoading === token.id}
                            style={btnStyle('#4f46e5', '#fff', '5px 10px', 11)}>↩ Restore</button>
                        )}
                        <span style={{
                          fontSize: 9, fontWeight: 700, textTransform: 'uppercase', textAlign: 'center',
                          padding: '2px 6px', borderRadius: 99,
                          background: isCalled ? (darkMode ? 'rgba(79,70,229,0.2)' : '#e0e7ff') : token.status === 'urgent' ? '#7c2d12' : (darkMode ? '#334155' : '#f1f5f9'),
                          color: isCalled ? '#4f46e5' : token.status === 'urgent' ? '#fb923c' : textMuted,
                        }}>
                          {token.status}
                        </span>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>

      {/* Footer brand */}
      <div style={{ padding: '10px 24px', borderTop: '1px solid #e2e8f0', textAlign: 'center', fontSize: 10, fontWeight: 700, letterSpacing: 1, color: '#94a3b8' }}>
        POWERED BY CLOUDAIRAMBO™
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        * { -webkit-tap-highlight-color: transparent; }
      `}</style>
    </div>
  );
}

// ── Style helpers ─────────────────────────────────────────────────────────────

const centerStyle: React.CSSProperties = {
  minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center',
  justifyContent: 'center', background: '#0f172a', color: '#fff', gap: 12,
  fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
};

function btnStyle(bg: string, color: string, padding: string, fontSize: number): React.CSSProperties {
  return {
    padding, borderRadius: 7, border: 'none', background: bg, color,
    fontWeight: 700, fontSize, cursor: 'pointer', whiteSpace: 'nowrap' as const,
  };
}
